/**
 * i18n maison (§10) — FR + EN dès le MVP.
 *
 * Trois garde-fous complémentaires contre les textes en dur / les traductions manquantes :
 *  1. `TranslationKey` est dérivé de `fr.json` : une clé inconnue ne compile pas ;
 *  2. `DICTIONARIES` est typé `Record<Locale, Dictionary>` : une clé absente de `en.json`
 *     ne compile pas non plus ;
 *  3. `npm run check:i18n` (lancé par `npm run build`) attrape les clés en trop et les
 *     divergences de paramètres d'interpolation entre les deux fichiers.
 *
 * Ce module est volontairement « navigateur uniquement » : il touche `document`,
 * `navigator` et `localStorage` sans garde d'existence. `src/core` reste, lui, exécutable
 * partout (§4) — c'est là que vit la logique testable hors DOM.
 */

import { formatInteger, formatNumber, formatPercent, splitDuration } from '@/core/format';
import type { LocaleCode } from '@/core/format';
import enMessages from './en.json';
import frMessages from './fr.json';

export const LOCALES = ['fr', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

/** Langue de repli imposée par le §10. */
export const FALLBACK_LOCALE: Locale = 'en';

const STORAGE_KEY = 'villopia.locale.v1';

type Dictionary = typeof frMessages;

type NestedKeys<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${NestedKeys<T[K]>}`;
}[keyof T & string];

/** Toutes les clés plates valides, du type `boot.loading`. */
export type TranslationKey = NestedKeys<Dictionary>;

export type TranslationParams = Readonly<Record<string, string | number>>;

const DICTIONARIES: Readonly<Record<Locale, Dictionary>> = {
  fr: frMessages,
  en: enMessages,
};

export type LocaleListener = (locale: Locale) => void;

const listeners = new Set<LocaleListener>();

let currentLocale: Locale = FALLBACK_LOCALE;

function isLocale(value: string | null | undefined): value is Locale {
  return value != null && (LOCALES as readonly string[]).includes(value);
}

/** `localStorage` lève en navigation privée sur certains navigateurs : jamais sans try/catch. */
function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Persistance impossible (mode privé, quota) : le jeu reste jouable, on ne bloque rien.
  }
}

/**
 * Langue préférée du navigateur, ramenée à une locale supportée.
 * `navigator.languages` est ordonné par préférence : on prend la première correspondance.
 */
export function detectBrowserLocale(): Locale {
  const preferred = navigator.languages;
  const ordered = preferred.length > 0 ? preferred : [navigator.language];

  for (const candidate of ordered) {
    const base = candidate.toLowerCase().split('-')[0];
    if (isLocale(base)) {
      return base;
    }
  }

  return FALLBACK_LOCALE;
}

function resolveMessage(dictionary: Dictionary, key: string): string | undefined {
  let node: unknown = dictionary;

  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) {
      return undefined;
    }
    node = (node as Record<string, unknown>)[segment];
  }

  return typeof node === 'string' ? node : undefined;
}

/** Remplace les jetons `{nom}` par leur valeur ; un jeton sans valeur est laissé intact. */
function interpolate(template: string, params: TranslationParams | undefined): string {
  if (params === undefined) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/** Locale active. */
/**
 * Le nom de ville par défaut, dans TOUTES les langues.
 *
 * ⚠️ Dérivé des dictionnaires, jamais écrit à la main (règle 15). Une liste recopiée se
 * désynchroniserait à la première traduction ajoutée — et le classement laisserait alors
 * passer des villes non nommées dans cette langue, ce que personne ne verrait avant que
 * les doublons ne reviennent.
 *
 * Sert au classement (une ville sans nom choisi n'y entre pas) et à la relance : les deux
 * doivent juger sur la MÊME liste, sinon on relance un joueur déjà classé, ou l'inverse.
 */
export function defaultCityNames(): readonly string[] {
  return LOCALES.map(
    (locale) => resolveMessage(DICTIONARIES[locale], 'city.defaultName') ?? '',
  ).filter((name) => name !== '');
}

export function getLocale(): Locale {
  return currentLocale;
}

/** Traduit une clé dans la locale active, avec repli EN puis sur la clé elle-même. */
export function t(key: TranslationKey, params?: TranslationParams): string {
  const message =
    resolveMessage(DICTIONARIES[currentLocale], key) ??
    resolveMessage(DICTIONARIES[FALLBACK_LOCALE], key);

  if (message === undefined) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] clé manquante : ${key}`);
    }
    return key;
  }

  return interpolate(message, params);
}

/** Nombre formaté dans la locale active (§6). */
export function n(value: number, options?: Parameters<typeof formatNumber>[2]): string {
  return formatNumber(value, currentLocale satisfies LocaleCode, options);
}

/** Entier exact formaté dans la locale active. */
export function int(value: number): string {
  return formatInteger(value, currentLocale);
}

/** Pourcentage formaté dans la locale active. */
export function percent(ratio: number): string {
  return formatPercent(ratio, currentLocale);
}

/**
 * Durée lisible, composée à partir des deux unités les plus significatives
 * (« 2 h 04 min », « 3 j 5 h »). La décomposition vient de `src/core`, les libellés d'ici.
 */
export function duration(milliseconds: number): string {
  const { days, hours, minutes, seconds, totalSeconds } = splitDuration(milliseconds);

  if (totalSeconds <= 0) {
    return t('duration.seconds', { count: 0 });
  }

  const parts: string[] = [];

  /*
    Une composante NULLE ne s'écrit pas : « 1 h 0 min » et « 30 min 0 s » se lisaient
    partout où une durée ronde s'affichait — la boutique de gemmes en tête (capture de
    Benoit). On garde au plus deux composantes, et seulement celles qui disent
    quelque chose ; « 1 h » est une durée finie, pas une durée tronquée.
  */
  if (days > 0) {
    parts.push(t('duration.days', { count: days }));
    if (hours > 0) parts.push(t('duration.hours', { count: hours }));
  } else if (hours > 0) {
    parts.push(t('duration.hours', { count: hours }));
    if (minutes > 0) parts.push(t('duration.minutes', { count: minutes }));
  } else if (minutes > 0) {
    parts.push(t('duration.minutes', { count: minutes }));
    if (seconds > 0) parts.push(t('duration.seconds', { count: seconds }));
  } else {
    parts.push(t('duration.seconds', { count: seconds }));
  }

  return parts.join(' ');
}

/** S'abonne aux changements de langue. Retourne la fonction de désabonnement. */
export function onLocaleChange(listener: LocaleListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Applique les traductions au DOM.
 *
 *   <span data-i18n="boot.loading"></span>
 *   <button data-i18n-attr="aria-label:common.close"></button>
 *
 * Volontairement basé sur `textContent` : aucune injection HTML possible depuis un dictionnaire.
 */
export function applyTranslations(root: ParentNode = document): void {
  for (const element of root.querySelectorAll<HTMLElement>('[data-i18n]')) {
    const key = element.dataset.i18n;
    if (key !== undefined) {
      element.textContent = t(key as TranslationKey);
    }
  }

  for (const element of root.querySelectorAll<HTMLElement>('[data-i18n-attr]')) {
    const bindings = element.dataset.i18nAttr;
    if (bindings === undefined) {
      continue;
    }
    for (const binding of bindings.split(',')) {
      const [attribute, key] = binding.split(':').map((part) => part.trim());
      if (attribute !== undefined && attribute !== '' && key !== undefined && key !== '') {
        element.setAttribute(attribute, t(key as TranslationKey));
      }
    }
  }
}

/** Change la langue active, la persiste, met à jour le DOM et notifie les abonnés. */
export function setLocale(locale: Locale): void {
  if (locale === currentLocale) {
    return;
  }

  currentLocale = locale;
  writeStoredLocale(locale);
  syncDocumentLocale();
  applyTranslations();

  for (const listener of listeners) {
    listener(locale);
  }
}

function syncDocumentLocale(): void {
  document.documentElement.lang = currentLocale;
  document.title = t('app.title');
}

/**
 * Initialise l'i18n : choix persisté > langue du navigateur > repli EN.
 * À appeler une seule fois, au tout début du démarrage.
 */
export function initI18n(): Locale {
  currentLocale = readStoredLocale() ?? detectBrowserLocale();
  syncDocumentLocale();
  return currentLocale;
}
